import { SKY_STAGES } from '../../../packages/domain/src/76-sky-campaign.mjs';
import { skyFlightCapabilities } from '../../../packages/domain/src/106-sky-capabilities.mjs';

/** Describe the active movement contract; camera phases never swap either hand. */
export function skyInputDescription(state, mirrored = false) {
  const left = mirrored ? 'RIGHT' : 'LEFT',
    right = mirrored ? 'LEFT' : 'RIGHT';
  const depth = state.freeFlight === true;
  if (SKY_STAGES[state.sector]?.kind === 'bonus')
    return {
      hint: `${left} PAD MOVES · FLY THROUGH GATES`,
      label: `Safe passage. ${left.toLowerCase()} pad or WASD moves the pilot. Fly through the gold gate, then follow the cyan gates. Collect gates to earn salvage for upgrades. P pauses.`,
    };
  if (state.orbitalFlight)
    return {
      hint: `${left} PAD ORBITS · ${right} PAD AIMS · AUTO FIRE`,
      label: `Orbital flight. Move the ${left.toLowerCase()} pad left or right to orbit the boss, and up or down to change altitude. A/D orbit; W/S rise or descend. The ${right.toLowerCase()} AIM pad moves the crosshairs independently. Drag to aim; lift to hold aim. Mouse or arrow keys aim. Automatic fire when weapons are ready. Space releases Nova. P pauses.`,
    };
  return {
    hint: `${left} PAD MOVES · ${right} PAD AIMS · AUTO FIRE`,
    label: depth
      ? 'Free flight. A/D move sideways, R/F rise or descend, W/S fly forward or back. Movement pad and Forward/Back cruise buttons move independently of the sight. Drag the AIM pad or use mouse/arrows to aim. Lift to hold aim. Space releases Nova. P pauses.'
      : `Sky Rider. ${left.toLowerCase()} pad moves the pilot; ${right.toLowerCase()} AIM pad moves the crosshairs. Drag to aim; lift to hold aim. A/D move sideways; W/S rise or descend. Mouse or arrow keys aim. Automatic fire when weapons are ready. Space releases Nova. P pauses.`,
  };
}

/** Compact safe-area flight ownership and immutable mastery progress. Text and
 * shapes retain meaning with reduced motion; domain clocks alone own rewards. */
export function createSkyFlightIndicators({ e, surface }) {
  const root = e('div', 'sky-flight-indicators');
  const ownership = e('p', 'sky-autopilot');
  const mastery = e('p', 'sky-mastery');
  ownership.setAttribute('role', 'status');
  mastery.setAttribute('aria-label', 'First-pass formation marks');
  root.append(ownership, mastery);
  surface.append(root);
  let wasReady = false,
    readyUntil = 0;
  return {
    paint(state, active) {
      const capabilities = skyFlightCapabilities(state);
      const collecting = SKY_STAGES[state.sector]?.kind === 'bonus';
      const ready = capabilities.weapons && state.boss?.aimWindow === true;
      if (ready && !wasReady) readyUntil = state.time + 1.4;
      wasReady = ready;
      ownership.hidden = !active || (!capabilities.label && !(ready && state.time < readyUntil));
      ownership.textContent = collecting
        ? '↗ MOVE · COLLECT'
        : capabilities.label
          ? `${capabilities.protected ? '◇' : '↗'} · ${capabilities.weapons ? '◎' : 'AIM'}`
          : '◎ SHOOT';
      ownership.setAttribute(
        'aria-label',
        collecting
          ? 'Move through the gates to collect salvage. Weapons are safe.'
          : capabilities.label || 'Weapons ready',
      );
      ownership.dataset.safe = String(capabilities.protected);
      const j = state.journey,
        charge = state.bossSupercharge;
      mastery.hidden = !active || !j?.waveCount;
      const marks = Array.from({ length: j?.waveCount ?? 0 }, (_, i) =>
        j.firstPassMarks?.[i] === true ? '◆' : j.firstPassMarks?.[i] === false ? '×' : '◇',
      ).join(' ');
      mastery.textContent =
        charge?.status === 'ready'
          ? `${marks} · ⚡`
          : charge?.status === 'active'
            ? `${marks} · ⚡ ${Math.ceil(charge.remaining)}s`
            : marks;
      mastery.dataset.charged = String(charge?.status === 'ready' || charge?.status === 'active');
      mastery.setAttribute(
        'aria-label',
        `${j?.firstPassMarks?.filter(Boolean).length ?? 0} of ${j?.waveCount ?? 0} first-pass formations. ${charge?.status === 'ready' ? 'Boss Supercharge ready.' : j?.masteryEligible === false ? 'Stage mastery missed; later waves still earn power-ups.' : 'Stage mastery available.'}`,
      );
    },
  };
}
